import { Link } from "react-router-dom";
import { Heart, Calendar, Users, Armchair, Images, ShieldCheck, Sparkles, ArrowRight, Quote, ListChecks, Wallet, Briefcase } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8d104b12-15fd-4fdb-a546-200c653be4ed/images/ffa41207cef5efb6d053e02157f029f98df13c112419e1289c1331ebead48844.jpeg";
const WEDDING_IMG = "https://images.unsplash.com/photo-1723832347953-83c28e2d4dd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const STORY_IMG = "https://images.unsplash.com/photo-1768767278997-136b49ce5d99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const GALLERY = [
  { label: "Weddings", src: WEDDING_IMG },
  { label: "Engagements", src: "https://images.unsplash.com/photo-1542286777-b6499aa99a0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Anniversaries & family", src: STORY_IMG },
  { label: "Baby showers & reveals", src: "https://images.unsplash.com/photo-1697539093652-c7716caeceb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "First & milestone birthdays", src: "https://images.unsplash.com/photo-1545696563-af8f6ec2295a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Coming of age", src: "https://images.unsplash.com/photo-1763959946841-33e3973b69ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Graduations", src: "https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Milestone religious events", src: "https://images.pexels.com/photos/16479518/pexels-photo-16479518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { label: "Retirements", src: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
];

const principles = [
  { icon: Heart, title: "Calm & celebratory", text: "Planning should feel like anticipation, not anxiety. Every screen is designed to bring a sense of ease." },
  { icon: ShieldCheck, title: "Privacy as a feature", text: "Your guest list, your gallery, your milestone. You decide exactly what is shared and with whom." },
  { icon: Sparkles, title: "Clarity over novelty", text: "Just the tools you need, thoughtfully arranged for the moment that matters, with no clutter and no gimmicks." },
];

const tools = [
  { icon: Users, title: "Guest List, RSVP & Thank-You", text: "Build your guest list free. Send beautiful invitations, collect RSVPs and meal choices, then track thank you notes, all in one flow." },
  { icon: ListChecks, title: "Event Planning Checklist", text: "A smart, date-based to-do list curated for your exact occasion, so nothing slips through the cracks." },
  { icon: Wallet, title: "Budget Tracker", text: "Set budgets and watch a live payment timeline that fills itself in from your vendors." },
  { icon: Briefcase, title: "Vendor Management", text: "Store quotes, contracts and invoices, and map out each vendor's payment plan in one place." },
  { icon: Armchair, title: "Seating Plan Creator", text: "Recreate your real venue to scale and drag guests onto resizable tables for the perfect layout." },
  { icon: Images, title: "Photo Gallery", text: "A shared gallery you and the guests you approve can revisit for a lifetime. You control what's seen." },
];

export default function Landing() {
  return (
    <div style={{ background: "#FDFBF7" }} className="min-h-screen">
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Heart size={20} fill="white" />
            <span className="font-serif text-2xl tracking-tight">Evermore</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-signin" className="text-sm text-white/90 hover:text-white transition-colors">Sign in</Link>
            <Link to="/login" data-testid="nav-getstarted" className="text-sm bg-white/95 text-stone-900 rounded-full px-5 py-2 hover:bg-white transition-colors">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[92vh] min-h-[560px]">
        <img src={HERO} alt="an elegantly decorated celebration venue" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />
        <div className="relative h-full max-w-6xl mx-auto px-6 flex flex-col justify-center text-white">
          <p className="overline text-white/80 fade-up">For all of life's milestones</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mt-4 max-w-3xl fade-up" style={{ animationDelay: "80ms" }}>
            Every milestone, beautifully remembered.
          </h1>
          <p className="mt-6 text-lg text-white/85 max-w-xl fade-up" style={{ animationDelay: "160ms" }}>
            From weddings and graduations to baby showers, birthdays and retirements, Evermore brings planning and memories into one calm, beautiful place for a lifetime.
          </p>
          <div className="flex flex-wrap gap-3 mt-9 fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/login" data-testid="hero-cta" className="inline-flex items-center gap-2 bg-white text-stone-900 rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#D4C5B3] transition-colors">
              Start planning for free <ArrowRight size={16} />
            </Link>
            <a href="#mission" className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-7 py-3.5 text-sm hover:bg-white/10 transition-colors">Our mission</a>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section id="mission" className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="overline">Our mission</p>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 leading-tight">
          To help you plan, celebrate and preserve every milestone that matters, then hold onto its memories for a lifetime.
        </h2>
        <p className="text-stone-600 text-lg leading-relaxed mt-8">
          Life is a series of moments worth remembering, from first birthdays and graduations to weddings, anniversaries and the quiet milestones in between.
          Evermore is built on a simple idea: technology should quietly hold the logistics so you can stay present for the celebration,
          then keep every photo and detail in one place you and your loved ones can return to forever.
        </p>
      </section>

      {/* Milestone gallery */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <p className="overline">Every kind of celebration</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4">One home for all of life's moments</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY.map((g, i) => (
            <div key={g.label} data-testid={`gallery-tile-${i}`}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]">
              <img src={g.src} alt={g.label} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
              <span className="absolute bottom-4 left-4 text-white font-serif text-xl sm:text-2xl drop-shadow">{g.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-5">
          {principles.map((p) => (
            <div key={p.title} className="bg-white border border-[#E8E6E1] rounded-xl p-8">
              <div className="w-11 h-11 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86]">
                <p.icon size={20} strokeWidth={1.6} />
              </div>
              <h3 className="font-serif text-2xl mt-5">{p.title}</h3>
              <p className="text-stone-500 text-sm leading-relaxed mt-2">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Vision */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <div>
          <p className="overline">Our vision</p>
          <h2 className="font-serif text-4xl tracking-tight mt-4 leading-tight">One platform for every meaningful celebration.</h2>
          <p className="text-stone-600 leading-relaxed mt-5">
            We started with weddings, the most joyful and most complex event of all. But our vision reaches across a whole lifetime:
            a single, trusted place to plan, share and preserve any occasion that brings people together.
          </p>
          <p className="text-stone-600 leading-relaxed mt-4">
            Templates get you started in minutes; thoughtful customization makes it unmistakably yours. And everyone you invite can keep the memories forever.
          </p>
          <div className="flex items-start gap-3 mt-8 border-l-2 border-[#D4C5B3] pl-5">
            <Quote size={18} className="text-[#D4C5B3] mt-1 shrink-0" />
            <p className="font-serif text-xl text-stone-700 italic">"The best planning tool is the one you forget you're using, because you're too busy enjoying the moment."</p>
          </div>
        </div>
      </section>

      {/* Planning tools */}
      <section className="bg-[#F5F4F0] border-y border-[#E8E6E1]">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <p className="overline">Everything you need</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4">The planning tools that do the work</h2>
            <p className="text-stone-500 mt-4 max-w-2xl mx-auto">Creating your event is free. Unlock the premium tools only when you need them. Everything below is built to make planning feel effortless.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tools.map((f) => (
              <div key={f.title} data-testid={`tool-card-${f.title.split(" ")[0].toLowerCase()}`} className="bg-white border border-[#E8E6E1] rounded-xl p-7">
                <div className="w-11 h-11 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86]">
                  <f.icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-xl mt-4">{f.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed mt-2">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Heart size={26} className="text-[#8C9A86] mx-auto mb-5" fill="#8C9A86" />
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Your next celebration starts here.</h2>
        <p className="text-stone-600 mt-5">Create your event workspace free, then unlock premium tools only when you need them.</p>
        <Link to="/login" data-testid="footer-cta" className="inline-flex items-center gap-2 mt-8 bg-[#2C2A28] text-white rounded-full px-8 py-4 text-sm font-medium hover:bg-[#8C9A86] transition-colors">
          Get started <ArrowRight size={16} />
        </Link>
      </section>

      <footer className="border-t border-[#E8E6E1] py-10 text-center">
        <div className="flex items-center justify-center gap-2 text-stone-400">
          <Heart size={14} fill="#8C9A86" className="text-[#8C9A86]" />
          <span className="font-serif text-lg">Evermore</span>
        </div>
        <p className="text-xs text-stone-400 mt-2">Plan · Celebrate · Share · Preserve</p>
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-1 text-sm text-stone-500" data-testid="footer-contact">
          <a href="mailto:info@evermore-events.net" className="hover:text-[#8C9A86] transition-colors">info@evermore-events.net</a>
          <a href="tel:+14169980918" className="hover:text-[#8C9A86] transition-colors">416-998-0918</a>
        </div>
      </footer>
    </div>
  );
}
